import { useMemo, useReducer } from "react";
import type { ListingFilters } from "../schemas/listingSchemas";

export interface MarketplaceFilters extends Required<Pick<ListingFilters, "query" | "packetReady" | "shortageOnly" | "brandOnly">> {
  shipsWithinHours?: number;
}

export type MarketplaceFilterAction =
  | { type: "setQuery"; query: string }
  | { type: "togglePacketReady" }
  | { type: "toggleShortageOnly" }
  | { type: "toggleBrandOnly" }
  | { type: "setShipsWithinHours"; hours?: number }
  | { type: "reset" };

export function createInitialMarketplaceFilters(): MarketplaceFilters {
  return {
    query: "",
    packetReady: false,
    shortageOnly: false,
    brandOnly: false,
    shipsWithinHours: undefined,
  };
}

export function reduceMarketplaceFilters(state: MarketplaceFilters, action: MarketplaceFilterAction): MarketplaceFilters {
  switch (action.type) {
    case "setQuery":
      return { ...state, query: action.query };
    case "togglePacketReady":
      return { ...state, packetReady: !state.packetReady };
    case "toggleShortageOnly":
      return { ...state, shortageOnly: !state.shortageOnly };
    case "toggleBrandOnly":
      return { ...state, brandOnly: !state.brandOnly };
    case "setShipsWithinHours":
      return { ...state, shipsWithinHours: action.hours };
    case "reset":
      return createInitialMarketplaceFilters();
  }
}

export function toListingFilters(filters: MarketplaceFilters): ListingFilters {
  return {
    query: filters.query.trim() || undefined,
    packetReady: filters.packetReady || undefined,
    shortageOnly: filters.shortageOnly || undefined,
    brandOnly: filters.brandOnly || undefined,
    shipsWithinHours: filters.shipsWithinHours,
  };
}

export function useMarketplaceFilters() {
  const [filters, dispatch] = useReducer(reduceMarketplaceFilters, undefined, createInitialMarketplaceFilters);
  const listingFilters = useMemo(() => toListingFilters(filters), [filters]);
  return { filters, listingFilters, dispatch };
}
